import React, { createContext } from 'react';
import { authApi as defaultAuthApi } from '../api/authApi';
import { trackAuthEvent } from '../analytics';
import {
  AUTH_ERROR_CODE,
  AUTH_EVENT,
  AUTH_STEP,
  AUTH_STORAGE_KEY,
  OTP_CHANNEL,
} from '../constants';
import { validateOtp, validatePhone, validateProfile } from '../utils/validators';

export const AuthContext = createContext(null);

/** True once login has written an access token, even before React re-renders. */
export function hasPersistedAuthSession() {
  try {
    return Boolean(localStorage.getItem(AUTH_STORAGE_KEY.ACCESS));
  } catch {
    return false;
  }
}

const EMPTY_PROFILE = { firstName: '', lastName: '' };

function readStoredSession() {
  try {
    const accessToken = localStorage.getItem(AUTH_STORAGE_KEY.ACCESS);
    const refreshToken = localStorage.getItem(AUTH_STORAGE_KEY.REFRESH);
    const rawProfile = localStorage.getItem(AUTH_STORAGE_KEY.PROFILE);
    const user = rawProfile ? JSON.parse(rawProfile) : null;
    return {
      accessToken: accessToken || null,
      refreshToken: refreshToken || null,
      user: user && typeof user === 'object' ? user : null,
    };
  } catch {
    return { accessToken: null, refreshToken: null, user: null };
  }
}

function writeStoredSession({ accessToken, refreshToken, user }) {
  try {
    if (accessToken) localStorage.setItem(AUTH_STORAGE_KEY.ACCESS, accessToken);
    else localStorage.removeItem(AUTH_STORAGE_KEY.ACCESS);

    if (refreshToken) localStorage.setItem(AUTH_STORAGE_KEY.REFRESH, refreshToken);
    else localStorage.removeItem(AUTH_STORAGE_KEY.REFRESH);

    if (user) localStorage.setItem(AUTH_STORAGE_KEY.PROFILE, JSON.stringify(user));
    else localStorage.removeItem(AUTH_STORAGE_KEY.PROFILE);
  } catch {
    // Private mode or a full quota. The in-memory session still updates.
  }
}

/** True only when the server rejected the token (401/403), not for 429, 5xx, or a network failure. */
function isRejectedToken(error) {
  return Boolean(error) && (error.status === 401 || error.status === 403);
}

/**
 * Phone → OTP → profile session for the auth feature.
 * Pass `api` in tests. `onAuthenticated` runs after a session is stored.
 * `onProfileComplete` runs when a verified phone still needs a name.
 */
export class AuthProvider extends React.Component {
  constructor(props) {
    super(props);
    const session = readStoredSession();
    this.api = props.api || defaultAuthApi;
    this.mounted = false;
    this.state = {
      step: AUTH_STEP.PHONE,
      phone: '',
      otp: '',
      profile: EMPTY_PROFILE,
      pendingProfile: null,
      user: session.user,
      accessToken: session.accessToken,
      refreshToken: session.refreshToken,
      loading: Boolean(session.accessToken),
      requiresPhone: false,
      signupTicket: null,
      socialTicket: null,
      socialEmail: null,
      channel: OTP_CHANNEL.SMS,
      errorCode: null,
      errorMessage: null,
    };
  }

  componentDidMount() {
    this.mounted = true;
    if (this.state.accessToken && typeof this.api.verify === 'function') {
      this.restoreSession();
    } else if (this.state.loading) {
      this.commit({ loading: false });
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  commit = (partial) => {
    if (this.mounted) {
      this.setState(partial);
    }
  };

  setPhone = (phone) => {
    this.commit({ phone, errorCode: null, errorMessage: null });
  };

  setOtp = (otp) => {
    this.commit({ otp, errorCode: null, errorMessage: null });
  };

  setProfileField = (field, value) => {
    if (field !== 'firstName' && field !== 'lastName') return;
    this.setState((current) => ({
      profile: { ...current.profile, [field]: value },
      errorCode: null,
      errorMessage: null,
    }));
  };

  backToPhone = () => {
    this.commit({
      step: AUTH_STEP.PHONE,
      otp: '',
      errorCode: null,
      errorMessage: null,
    });
  };

  resetFlow = () => {
    this.commit({
      step: AUTH_STEP.PHONE,
      phone: '',
      otp: '',
      profile: EMPTY_PROFILE,
      pendingProfile: null,
      loading: false,
      errorCode: null,
      errorMessage: null,
    });
  };

  submitPhone = async () => {
    const result = validatePhone(this.state.phone);
    if (!result.ok) {
      this.commit({ errorCode: result.errorCode, errorMessage: null });
      trackAuthEvent(AUTH_EVENT.LOGIN_FAILED, {
        step: AUTH_STEP.PHONE,
        errorCode: result.errorCode,
      });
      return false;
    }

    this.commit({
      loading: true,
      errorCode: null,
      errorMessage: null,
      phone: result.value,
    });

    try {
      await this.api.login(result.value);
      this.commit({ loading: false, step: AUTH_STEP.OTP, otp: '', phone: result.value });
      trackAuthEvent(AUTH_EVENT.OTP_SENT, { step: AUTH_STEP.OTP });
      return true;
    } catch (error) {
      const errorCode = error.code || AUTH_ERROR_CODE.REQUEST_FAILED;
      this.commit({
        loading: false,
        errorCode,
        errorMessage: error.message || null,
      });
      trackAuthEvent(AUTH_EVENT.LOGIN_FAILED, { step: AUTH_STEP.PHONE, errorCode });
      return false;
    }
  };

  submitOtp = async () => {
    const result = validateOtp(this.state.otp);
    if (!result.ok) {
      this.commit({ errorCode: result.errorCode, errorMessage: null });
      trackAuthEvent(AUTH_EVENT.LOGIN_FAILED, {
        step: AUTH_STEP.OTP,
        errorCode: result.errorCode,
      });
      return false;
    }

    this.commit({ loading: true, errorCode: null, errorMessage: null });

    try {
      const data = await this.api.verifyOtp(this.state.phone, result.value);
      if (data && data.needsSignup) {
        this.commit({
          loading: false,
          step: AUTH_STEP.PROFILE,
          otp: result.value,
          signupTicket: data.signupTicket || null,
        });
        trackAuthEvent(AUTH_EVENT.OTP_VERIFIED, { step: AUTH_STEP.PROFILE });
        return true;
      }

      const session = this.storeSession(data);
      this.commit({
        loading: false,
        step: AUTH_STEP.PHONE,
        phone: '',
        otp: '',
        errorCode: null,
        errorMessage: null,
        ...session,
      });
      trackAuthEvent(AUTH_EVENT.OTP_VERIFIED, { step: AUTH_STEP.OTP });
      if (typeof this.props.onAuthenticated === 'function') {
        this.props.onAuthenticated(session.user);
      }
      return true;
    } catch (error) {
      const errorCode = error.code || AUTH_ERROR_CODE.REQUEST_FAILED;
      this.commit({
        loading: false,
        errorCode,
        errorMessage: error.message || null,
      });
      trackAuthEvent(AUTH_EVENT.LOGIN_FAILED, { step: AUTH_STEP.OTP, errorCode });
      return false;
    }
  };

  submitProfile = () => {
    const result = validateProfile(this.state.profile);
    if (!result.ok) {
      this.commit({ errorCode: result.errorCode, errorMessage: null });
      trackAuthEvent(AUTH_EVENT.LOGIN_FAILED, {
        step: AUTH_STEP.PROFILE,
        errorCode: result.errorCode,
      });
      return false;
    }

    const pendingProfile = { ...result.value, phone: this.state.phone };
    this.commit({ pendingProfile, errorCode: null, errorMessage: null });
    trackAuthEvent(AUTH_EVENT.PROFILE_COMPLETED, { step: AUTH_STEP.PROFILE });
    if (typeof this.props.onProfileComplete === 'function') {
      this.props.onProfileComplete(pendingProfile);
    }
    return true;
  };

  restoreSession = async () => {
    try {
      const data = await this.api.verify(this.state.accessToken);
      // A mobile number is optional, so a session without one is kept; requiresPhone
      // only lets the Account page and the dialog offer to add it.
      if (data && data.success && data.user) {
        writeStoredSession({
          accessToken: this.state.accessToken,
          refreshToken: this.state.refreshToken,
          user: data.user,
        });
        this.commit({
          user: data.user,
          loading: false,
          requiresPhone: Boolean(data.requiresPhone),
        });
        return;
      }
    } catch (error) {
      // Only the server saying the token is no good ends the session. A rate limit,
      // a server error, or no network keeps the stored login for the next page load.
      if (!isRejectedToken(error)) {
        this.commit({ loading: false });
        return;
      }
    }
    writeStoredSession({ accessToken: null, refreshToken: null, user: null });
    this.commit({
      user: null,
      accessToken: null,
      refreshToken: null,
      loading: false,
      requiresPhone: false,
    });
  };

  sendLoginOtp = (phone, channel = OTP_CHANNEL.SMS) => {
    this.commit({ channel, phone });
    return this.api.login(phone, channel === OTP_CHANNEL.SMS ? undefined : channel);
  };

  verifyLoginOtp = async (phone, otp, socialTicket) => {
    const data = await this.api.verifyOtp(phone, otp, socialTicket);
    if (data && (data.accessToken || data.token) && data.user) {
      const session = this.storeSession(data);
      this.commit({
        ...session,
        requiresPhone: Boolean(data.requiresPhone),
        signupTicket: null,
        socialTicket: null,
        loading: false,
        errorCode: null,
        errorMessage: null,
      });
    } else if (data && data.needsSignup) {
      this.commit({ signupTicket: data.signupTicket || null, phone, loading: false });
    }
    return data;
  };

  completePhoneSignup = async ({ firstName, email }) => {
    const data = await this.api.completePhoneSignup({
      signupTicket: this.state.signupTicket,
      firstName,
      email,
    });
    if (data && (data.accessToken || data.token) && data.user) {
      const session = this.storeSession(data);
      this.commit({
        ...session,
        signupTicket: null,
        socialTicket: null,
        requiresPhone: false,
        step: AUTH_STEP.PHONE,
        loading: false,
      });
    }
    return data;
  };

  googleLogin = async (credential) => {
    const data = await this.api.google(credential);
    return this.applyIdentity(data);
  };

  appleLogin = async (identityToken, fullName) => {
    const data = await this.api.apple(identityToken, fullName);
    return this.applyIdentity(data);
  };

  applyIdentity(data = {}) {
    // Google/Apple now sign in at once, phone or not (requiresPhone only offers to add one).
    // A ticket without a session is the older server's "verify a phone first" answer.
    const pendingPhone = Boolean(data.socialTicket) && !(data.accessToken || data.token);
    if (pendingPhone) {
      this.commit({
        socialTicket: data.socialTicket || null,
        socialEmail: data.email || null,
        requiresPhone: true,
        user: null,
        accessToken: null,
      });
      return data;
    }
    if ((data.accessToken || data.token) && data.user) {
      const session = this.storeSession(data);
      this.commit({
        ...session,
        socialTicket: null,
        socialEmail: data.user.email || null,
        requiresPhone: Boolean(data.requiresPhone),
      });
    }
    return data;
  }

  addPhone = (phone, channel) => {
    const accessToken = this.state.accessToken;
    return this.api.addPhone(phone, accessToken, channel);
  };

  verifyLinkedPhone = async (phone, otp) => {
    const data = await this.api.verifyPhone(phone, otp, this.state.accessToken);
    if (data && (data.accessToken || data.token) && data.user) {
      const session = this.storeSession(data);
      this.commit({ ...session, requiresPhone: false });
      return data;
    }
    if (data && data.success) {
      const user = data.user || { ...(this.state.user || {}), phone_number: data.phone_number || phone };
      writeStoredSession({
        accessToken: this.state.accessToken,
        refreshToken: this.state.refreshToken,
        user,
      });
      this.commit({ user, requiresPhone: false });
    }
    return data;
  };

  /**
   * Run a logged-in call. The Account page can stay open past the access token's
   * life, so a 401 refreshes the session once and retries.
   */
  withFreshToken = async (call) => {
    try {
      return await call(this.state.accessToken);
    } catch (error) {
      if (!error || error.status !== 401 || !this.state.refreshToken) throw error;
      const refreshed = await this.refreshSession();
      return call(refreshed.accessToken || refreshed.token);
    }
  };

  sendEmailOtp = (email) => this.withFreshToken((token) => this.api.sendEmailOtp(email, token));

  /** Saves the verified email on the stored user, so the header and checkout see it. */
  verifyEmailOtp = async (email, otp) => {
    const data = await this.withFreshToken((token) => this.api.verifyEmailOtp(email, otp, token));
    if (data && data.success && data.user) {
      const user = { ...(this.state.user || {}), ...data.user };
      writeStoredSession({
        accessToken: this.state.accessToken,
        refreshToken: this.state.refreshToken,
        user,
      });
      this.commit({ user });
    }
    return data;
  };

  sendPhoneChangeOtp = (phone, channel) => this.withFreshToken(
    (token) => this.api.sendPhoneChangeOtp(phone, token, channel),
  );

  /**
   * The server signs out every other session when the number changes, so the old token
   * stops working: keep the fresh session it returns, with the updated user.
   */
  verifyPhoneChange = async (phone, otp) => {
    const data = await this.withFreshToken((token) => this.api.verifyPhoneChange(phone, otp, token));
    if (data && data.success && data.user && (data.accessToken || data.token)) {
      const session = this.storeSession({ ...data, user: { ...(this.state.user || {}), ...data.user } });
      this.commit({ ...session, requiresPhone: false });
    }
    return data;
  };

  setRequiresPhone = (requiresPhone) => {
    this.commit({ requiresPhone });
  };

  /** Drop signup/social tickets the server no longer accepts. A logged-in session is kept. */
  clearPendingSignup = () => {
    const isAuthenticated = Boolean(this.state.user && this.state.accessToken);
    this.commit({
      signupTicket: null,
      socialTicket: null,
      socialEmail: isAuthenticated ? this.state.socialEmail : null,
      requiresPhone: isAuthenticated ? this.state.requiresPhone : false,
    });
  };

  refreshSession = async () => {
    if (!this.state.refreshToken) {
      const errorCode = AUTH_ERROR_CODE.UNAUTHORIZED;
      this.commit({ errorCode, errorMessage: null });
      throw Object.assign(new Error('Missing refresh token'), { code: errorCode });
    }

    try {
      const data = await this.api.refreshToken(this.state.refreshToken);
      const session = this.storeSession(data);
      this.commit({ ...session, errorCode: null, errorMessage: null });
      return data;
    } catch (error) {
      const errorCode = error.code || AUTH_ERROR_CODE.REQUEST_FAILED;
      this.commit({ errorCode, errorMessage: error.message || null });
      throw error;
    }
  };

  logout = async () => {
    const { refreshToken, accessToken } = this.state;
    await this.api.logout({ refreshToken, accessToken });
    writeStoredSession({ accessToken: null, refreshToken: null, user: null });
    this.commit({
      user: null,
      accessToken: null,
      refreshToken: null,
      step: AUTH_STEP.PHONE,
      phone: '',
      otp: '',
      profile: EMPTY_PROFILE,
      pendingProfile: null,
      loading: false,
      errorCode: null,
      errorMessage: null,
    });
    trackAuthEvent(AUTH_EVENT.LOGOUT);
  };

  storeSession(data = {}) {
    const accessToken = data.accessToken || data.token || null;
    const refreshToken = data.refreshToken || null;
    const user = Object.prototype.hasOwnProperty.call(data, 'user')
      ? data.user
      : this.state.user;
    writeStoredSession({ accessToken, refreshToken, user });
    return { accessToken, refreshToken, user };
  }

  render() {
    return (
      <AuthContext.Provider value={this.contextValue()}>
        {this.props.children}
      </AuthContext.Provider>
    );
  }

  contextValue() {
    return {
      step: this.state.step,
      phone: this.state.phone,
      otp: this.state.otp,
      profile: this.state.profile,
      pendingProfile: this.state.pendingProfile,
      user: this.state.user,
      token: this.state.accessToken,
      accessToken: this.state.accessToken,
      refreshToken: this.state.refreshToken,
      isAuthenticated: Boolean(this.state.user && this.state.accessToken),
      loading: this.state.loading,
      requiresPhone: this.state.requiresPhone,
      signupTicket: this.state.signupTicket,
      socialTicket: this.state.socialTicket,
      socialEmail: this.state.socialEmail,
      errorCode: this.state.errorCode,
      errorMessage: this.state.errorMessage,
      setPhone: this.setPhone,
      setOtp: this.setOtp,
      setProfileField: this.setProfileField,
      backToPhone: this.backToPhone,
      resetFlow: this.resetFlow,
      submitPhone: this.submitPhone,
      submitOtp: this.submitOtp,
      submitProfile: this.submitProfile,
      sendLoginOtp: this.sendLoginOtp,
      verifyLoginOtp: this.verifyLoginOtp,
      completePhoneSignup: this.completePhoneSignup,
      googleLogin: this.googleLogin,
      appleLogin: this.appleLogin,
      addPhone: this.addPhone,
      verifyLinkedPhone: this.verifyLinkedPhone,
      sendEmailOtp: this.sendEmailOtp,
      verifyEmailOtp: this.verifyEmailOtp,
      sendPhoneChangeOtp: this.sendPhoneChangeOtp,
      verifyPhoneChange: this.verifyPhoneChange,
      setRequiresPhone: this.setRequiresPhone,
      clearPendingSignup: this.clearPendingSignup,
      refreshSession: this.refreshSession,
      logout: this.logout,
    };
  }
}
